import { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const breaches = [
  {
    company: 'PlayDapp',
    date: 'February 2024',
    amount: 290_000_000,
    description: 'Smart contract access control flaw allowed unauthorized minting of 1.79 billion PLA tokens.',
    highlight: 'Smart contract vulnerability',
  },
  {
    company: 'DMM Bitcoin',
    date: 'May 2024',
    amount: 305_000_000,
    description: 'Japanese exchange lost 4,502 BTC through compromised private key. Forced to shut down entirely in December 2024.',
    highlight: 'Exchange shut down permanently',
  },
  {
    company: 'BtcTurk',
    date: 'June 2024',
    amount: 55_000_000,
    description: 'Turkish exchange hot wallets compromised via stolen private keys. Binance helped freeze $5.3M of stolen funds.',
    highlight: 'Hot wallet breach',
  },
  {
    company: 'UwU Lend',
    date: 'June 2024',
    amount: 20_000_000,
    description: 'Flash loan attack manipulated price oracle. Entire exploit executed within 7 minutes.',
    highlight: 'Exploited in 7 minutes',
  },
  {
    company: 'WazirX',
    date: 'July 2024',
    amount: 235_000_000,
    description: 'Indian exchange multisig wallet compromised. Attacker planted fake smart contract 8 days before attack. Attributed to Lazarus Group.',
    highlight: 'Multisig wallet breach',
  },
  {
    company: 'Radiant Capital',
    date: 'October 2024',
    amount: 58_000_000,
    description: 'Attackers bypassed 3-of-11 multisig via malware that altered transaction data on Gnosis Safe interface. Lazarus Group attributed.',
    highlight: 'Multisig bypass via malware',
  },
  {
    company: 'Phemex',
    date: 'January 2025',
    amount: 85_000_000,
    description: 'Singapore exchange hot wallet breach. Losses revised upward as investigation progressed. Withdrawals suspended for weeks.',
    highlight: 'Hot wallet compromise',
  },
  {
    company: 'Bybit',
    date: 'February 2025',
    amount: 1_400_000_000,
    description: 'North Korean hackers (Lazarus Group) compromised a cold wallet via manipulated multisig transaction through Safe{Wallet} developer machine. Largest crypto hack in history.',
    highlight: 'Largest crypto hack ever',
    isMajor: true,
  },
  {
    company: 'Infini',
    date: 'February 2025',
    amount: 49_500_000,
    description: 'Stablecoin neobank exploited via developer privilege misuse. Insider/governance failure.',
    highlight: 'Developer privilege exploit',
  },
  {
    company: 'Cetus',
    date: 'May 2025',
    amount: 223_000_000,
    description: 'DEX on Sui blockchain exploited via spoofed token contracts that tricked liquidity pools. $223M drained at peak impact.',
    highlight: 'Spoofed token exploit',
  },
  {
    company: 'Nobitex',
    date: 'June 2025',
    amount: 90_000_000,
    description: 'Iranian exchange hacked, funds sent to irrecoverable addresses with anti-government messages. Politically motivated.',
    highlight: 'Politically motivated attack',
  },
  {
    company: 'Balancer v2',
    date: 'November 2025',
    amount: 129_000_000,
    description: 'DeFi protocol pools exploited - 5 years after launch. Composable stable pools vulnerability.',
    highlight: '5 years of hidden vulnerability',
  },
];

const summaryStats = [
  { value: '$2.2B', label: 'Stolen in 2024', source: 'Chainalysis' },
  { value: '$3.4B', label: 'Stolen in 2025', source: '↑37% YoY' },
  { value: '303', label: 'Incidents in 2024', source: '' },
  { value: '$269.5M', label: 'Smart contract losses', source: 'Hacken, 2024' },
];

function useCountUp(target: number, duration = 2000, trigger = false) {
  const [count, setCount] = useState(0);
  const frameRef = useRef<number>();

  useEffect(() => {
    if (!trigger) return;
    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [target, duration, trigger]);

  return count;
}

function formatAmount(n: number) {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(0)}M`;
  return `$${n.toLocaleString()}`;
}

/* ─── Timeline Node ─── */
const TimelineNode = ({
  breach,
  index,
}: {
  breach: typeof breaches[0];
  index: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const count = useCountUp(breach.amount, 1800 + index * 120, visible);
  const isAbove = index % 2 === 0;
  const isMajor = 'isMajor' in breach && breach.isMajor;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3, root: el.closest('[data-scroll-container]') }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const dotSize = isMajor ? 'w-5 h-5' : 'w-3.5 h-3.5';
  const glowSize = isMajor
    ? 'shadow-[0_0_20px_hsl(0_88%_63%/0.7),0_0_40px_hsl(0_88%_63%/0.3)]'
    : 'shadow-[0_0_12px_hsl(0_88%_63%/0.5)]';

  const cardBorder = isMajor
    ? 'border-primary/30 hover:border-primary/50'
    : 'border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.18)]';

  const cardGlow = isMajor
    ? 'hover:shadow-[0_0_30px_hsl(0_88%_63%/0.15)]'
    : 'hover:shadow-[0_0_20px_hsl(0_88%_63%/0.08)]';

  const card = (
    <div
      className={`
        w-[260px] sm:w-[280px] rounded-xl border bg-[hsl(0_0%_7%)] p-5
        transition-all duration-700
        ${cardBorder} ${cardGlow}
        ${visible ? 'opacity-100 translate-y-0' : `opacity-0 ${isAbove ? 'translate-y-6' : '-translate-y-6'}`}
      `}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <p className="text-xs text-muted-foreground mb-1.5">{breach.date}</p>
      <h3 className={`font-semibold text-foreground mb-1 ${isMajor ? 'text-xl' : 'text-lg'}`}>
        {breach.company}
      </h3>
      <p className={`font-bold text-primary tabular-nums mb-2.5 font-numbers ${isMajor ? 'text-4xl lg:text-5xl' : 'text-3xl lg:text-4xl'}`}>
        {formatAmount(count)}
      </p>
      <p className="text-xs text-muted-foreground leading-relaxed mb-3">
        {breach.description}
      </p>
      <span className="inline-block text-[11px] font-medium text-muted-foreground bg-secondary/60 border border-border px-2.5 py-1 rounded-full">
        {breach.highlight}
      </span>
    </div>
  );

  const spacerW = isMajor ? 'w-[260px] sm:w-[280px]' : 'w-[260px] sm:w-[280px]';

  return (
    <div
      ref={ref}
      className="flex flex-col items-center flex-shrink-0"
      
    >
      {/* Above: card or spacer */}
      <div className={`flex items-end justify-center h-[280px] sm:h-[300px] pb-4`}>
        {isAbove ? card : <div className={spacerW} />}
      </div>

      {/* Dot on the line */}
      <div className="relative flex items-center justify-center h-8 z-10">
        <div
          className={`${dotSize} rounded-full border-2 border-primary bg-background ${glowSize} animate-pulse-dot`}
        />
      </div>

      {/* Below: card or spacer */}
      <div className={`flex items-start justify-center h-[280px] sm:h-[300px] pt-4`}>
        {!isAbove ? card : <div className={spacerW} />}
      </div>
    </div>
  );
};

/* ─── Scroll Progress ─── */
const ScrollProgress = ({ progress, total }: { progress: number; total: number }) => {
  return (
    <div className="flex items-center justify-center gap-1.5 mt-6">
      {Array.from({ length: total }).map((_, i) => {
        const segmentProgress = progress * (total - 1);
        const isActive = Math.abs(segmentProgress - i) < 0.8;
        return (
          <div
            key={i}
            className={`h-1 rounded-full transition-all duration-300 ${
              isActive ? 'w-4 bg-primary/70' : 'w-1.5 bg-border'
            }`}
          />
        );
      })}
    </div>
  );
};

/* ─── Swipe Hint ─── */
const SwipeHint = () => {
  const [show, setShow] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShow(false), 2500);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 text-muted-foreground text-sm animate-bounce-x z-20 pointer-events-none">
      <span>swipe to explore</span>
      <ChevronRight className="w-4 h-4" />
    </div>
  );
};

/* ─── Main Component ─── */
const BreachShowcase = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const isMobile = useIsMobile();
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const dragRafRef = useRef<number | null>(null);
  const dragCurrentRef = useRef(0);
  const dragTargetRef = useRef(0);

  const animateDrag = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      dragRafRef.current = null;
      return;
    }

    const delta = dragTargetRef.current - dragCurrentRef.current;
    dragCurrentRef.current += delta * 0.18;
    el.scrollLeft = dragCurrentRef.current;

    if (Math.abs(delta) <= 0.5) {
      dragCurrentRef.current = dragTargetRef.current;
      el.scrollLeft = dragCurrentRef.current;
      dragRafRef.current = null;
      return;
    }

    dragRafRef.current = requestAnimationFrame(animateDrag);
  }, []);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setScrollProgress(max > 0 ? el.scrollLeft / max : 0);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Wheel event removed to allow normal vertical scrolling
    // Removed wheel hijacking - let page scroll naturally
  }, []);

  useEffect(() => {
    return () => {
      if (dragRafRef.current !== null) {
        cancelAnimationFrame(dragRafRef.current);
      }
    };
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;

    isDragging.current = true;
    startX.current = e.pageX - el.offsetLeft;
    scrollLeft.current = el.scrollLeft;
    dragCurrentRef.current = el.scrollLeft;
    dragTargetRef.current = el.scrollLeft;

    if (dragRafRef.current !== null) {
      cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = null;
    }

    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
    // no snap
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return;
    const el = scrollRef.current;
    if (!el) return;

    const x = e.pageX - el.offsetLeft;
    dragTargetRef.current = scrollLeft.current - (x - startX.current);

    if (dragRafRef.current === null) {
      dragRafRef.current = requestAnimationFrame(animateDrag);
    }
  }, [animateDrag]);

  const onMouseUp = useCallback(() => {
    isDragging.current = false;
    const el = scrollRef.current;
    if (el) {
      el.style.cursor = 'grab';
      el.style.userSelect = '';

      // Stop any running animation immediately — stay exactly where released
      if (dragRafRef.current !== null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
      // Sync refs to current position so no further drift
      dragCurrentRef.current = el.scrollLeft;
      dragTargetRef.current = el.scrollLeft;
    }
  }, []);

  const scrollBy = useCallback((dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: 'smooth' });
  }, []);

  return (
    <section className="py-[75px] relative overflow-hidden" id="breaches">
      <div className="absolute inset-0 bg-gradient-radial-red opacity-30 pointer-events-none" />

      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-[55px] px-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-[35px]">
            They thought they were{' '}
            <span className="text-gradient-red font-normal">safe</span> too
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-numbers">
            In 2024-2025, hackers stole over $6.1 billion in crypto. These are
            the most devastating cases.
          </p>
        </div>

        {/* Horizontal scroll timeline */}
        <div className="relative">
          {/* Arrow buttons */}
          {!isMobile && (
            <>
              <button
                onClick={() => scrollBy(-1)}
                className="absolute left-2 lg:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-card/80 border border-border backdrop-blur-sm flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => scrollBy(1)}
                className="absolute right-2 lg:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-card/80 border border-border backdrop-blur-sm flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Scroll container */}
          <div
            ref={scrollRef}
            data-scroll-container
            className="overflow-x-auto overflow-y-hidden cursor-grab scrollbar-hide relative"
            style={{
              WebkitOverflowScrolling: 'touch',
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            <div className="inline-flex gap-3 sm:gap-4 px-4 lg:px-8 relative">
              {/* Timeline line — spans full width of the inline-flex */}
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-border pointer-events-none z-0" />

              {breaches.map((breach, i) => (
                <TimelineNode key={breach.company} breach={breach} index={i} />
              ))}
            </div>

            {isMobile && <SwipeHint />}
          </div>

          {/* Dot progress */}
          <ScrollProgress progress={scrollProgress} total={breaches.length} />
        </div>

        {/* ─── Divider + Summary Stats ─── */}
        <div className="mt-16 lg:mt-20 px-4 lg:px-8">
          {/* Red divider line */}
          <div className="gradient-red-line h-px w-full max-w-4xl mx-auto mb-8" />

          {/* Stats bar */}
          <div
            ref={statsRef}
            className="max-w-5xl mx-auto rounded-2xl border border-border bg-[hsl(0_0%_6%)] p-8 lg:p-10 shadow-[0_-8px_40px_hsl(0_0%_0%/0.5)]"
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
              {summaryStats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div
                    className={`text-3xl md:text-4xl lg:text-5xl font-bold text-primary font-numbers transition-all duration-700 ${
                      statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                  >
                    {stat.value}
                  </div>
                  <p className="text-sm text-foreground mt-2">{stat.label}</p>
                  {stat.source && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {stat.source}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BreachShowcase;
